import { useCallback, useEffect, useRef, useState } from 'react'
import { ChartAutoSaveStatus } from '@utils/chart-save.utils'
import type { ChartingRouteTab } from '@pages/Charting/chartingNoteOverlay.util'

const METADATA_DEBOUNCE_MS = 60_000
const CONDITION_DEBOUNCE_MS = 2_500
const SAVED_INDICATOR_MS = 3_000

interface UseChartAutoSaveParams {
  enabled: boolean
  chartingTab: ChartingRouteTab
  isDentalUpdated: boolean
  isPerioUpdated: boolean
  dentalSignature: string
  perioSignature: string
  canAutoSaveDental: boolean
  canAutoSavePerio: boolean
  onAutoSave: () => Promise<boolean>
}

interface UseChartAutoSaveResult {
  status: ChartAutoSaveStatus
  triggerAutoSave: () => Promise<boolean>
}

export function useChartAutoSave({
  enabled,
  chartingTab,
  isDentalUpdated,
  isPerioUpdated,
  dentalSignature,
  perioSignature,
  canAutoSaveDental,
  canAutoSavePerio,
  onAutoSave,
}: UseChartAutoSaveParams): UseChartAutoSaveResult {
  const [status, setStatus] = useState<ChartAutoSaveStatus>('idle')
  const isSavingRef = useRef(false)
  const lastSavedSignatureRef = useRef<string | null>(null)
  const metadataTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const conditionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const onAutoSaveRef = useRef(onAutoSave)
  onAutoSaveRef.current = onAutoSave

  const activeSignature =
    chartingTab === 'home' ? dentalSignature : perioSignature
  const isUpdated = chartingTab === 'home' ? isDentalUpdated : isPerioUpdated
  const canAutoSave =
    chartingTab === 'home' ? canAutoSaveDental : canAutoSavePerio

  const clearTimers = useCallback((): void => {
    if (metadataTimerRef.current) {
      clearTimeout(metadataTimerRef.current)
      metadataTimerRef.current = null
    }
    if (conditionTimerRef.current) {
      clearTimeout(conditionTimerRef.current)
      conditionTimerRef.current = null
    }
  }, [])

  const triggerAutoSave = useCallback(async (): Promise<boolean> => {
    if (!enabled || isSavingRef.current || !canAutoSave) {
      return false
    }
    if (activeSignature === lastSavedSignatureRef.current) {
      return true
    }
    isSavingRef.current = true
    setStatus('saving')
    try {
      const saved = await onAutoSaveRef.current()
      if (saved) {
        lastSavedSignatureRef.current = activeSignature
        setStatus('saved')
        if (savedIndicatorTimerRef.current) {
          clearTimeout(savedIndicatorTimerRef.current)
        }
        savedIndicatorTimerRef.current = setTimeout(() => {
          setStatus('idle')
        }, SAVED_INDICATOR_MS)
      } else {
        setStatus('error')
      }
      return saved
    } catch {
      setStatus('error')
      return false
    } finally {
      isSavingRef.current = false
    }
  }, [activeSignature, canAutoSave, enabled])

  useEffect(() => {
    if (!enabled || !isUpdated || !canAutoSave) {
      clearTimers()
      return
    }
    if (activeSignature === lastSavedSignatureRef.current) {
      return
    }
    clearTimers()
    metadataTimerRef.current = setTimeout(() => {
      void triggerAutoSave()
    }, METADATA_DEBOUNCE_MS)
    conditionTimerRef.current = setTimeout(() => {
      void triggerAutoSave()
    }, CONDITION_DEBOUNCE_MS)
    return clearTimers
  }, [
    activeSignature,
    canAutoSave,
    clearTimers,
    enabled,
    isUpdated,
    triggerAutoSave,
  ])

  useEffect(() => {
    return () => {
      clearTimers()
      if (savedIndicatorTimerRef.current) {
        clearTimeout(savedIndicatorTimerRef.current)
      }
    }
  }, [clearTimers])

  useEffect(() => {
    if (!isUpdated) {
      lastSavedSignatureRef.current = null
      if (status === 'saved') {
        setStatus('idle')
      }
    }
  }, [isUpdated, status])

  return { status, triggerAutoSave }
}
